import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartModule } from 'primeng/chart';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { RouterModule } from '@angular/router';
import { InMemoryCacheService, isGoogleAuthorizationError, UtilsService } from 'src/app/common';

export interface GscKpi {
  current: number;
  previous: number;
  delta: number;
  deltaPercent: number;
  sparklineValues: number[];
  sparklineDates: string[];
}

export interface GscSummary {
  clicks: GscKpi;
  impressions: GscKpi;
  ctr: GscKpi;
  avgPosition: GscKpi;
}

export interface GscDashboardState {
  connected: boolean;
  error: boolean;
  authorizationRequired: boolean;
  summary: GscSummary | null;
}

@Component({
  selector: 'gig-gsc-dashboard',
  standalone: true,
  imports: [CommonModule, ChartModule, CardModule, TooltipModule, RouterModule],
  templateUrl: './gsc-dashboard.component.html',
  styleUrls: ['./gsc-dashboard.component.scss']
})
export class GscDashboardComponent implements OnInit {

  @Output() public readonly stateChange = new EventEmitter<GscDashboardState>();

  public isConnected: boolean = false;
  public isLoading: boolean = false;
  public hasError: boolean = false;
  public authorizationRequired: boolean = false;
  public summary: GscSummary | null = null;
  private project: any = null;

  public readonly tooltips = {
    clicks: 'Visits from Google Search results — someone clicked your link in Google.',
    impressions: 'Times your site appeared in Google Search results, whether clicked or not.',
    ctr: 'Click-through rate: clicks divided by impressions. Higher is better.',
    avgPosition: 'Your average ranking placement in Google Search results. Lower numbers are better (1 = top result). Only applicable to Search, not other Google surfaces.'
  };

  constructor(
    private cache: InMemoryCacheService,
    private utils: UtilsService
  ) {}

  public async ngOnInit(): Promise<void> {
    this.project = await this.resolveSelectedProject();
    const google = this.project?.externalProviders?.google;
    const siteUrl = google?.siteUrl;
    const isConfigured = !!(siteUrl && siteUrl.trim() !== '');
    const hasAuthorization = !!(google?.accessToken || google?.refreshToken);
    this.authorizationRequired = isConfigured && !hasAuthorization;
    this.isConnected = isConfigured && hasAuthorization;

    if (!this.isConnected) {
      this.emitState();
      return;
    }

    await this.loadData();
  }

  public buildSparklineChart(values: number[], color: string): any {
    return {
      labels: new Array(values.length).fill(''),
      datasets: [
        {
          data: values,
          borderColor: color,
          backgroundColor: `${color}22`,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          borderWidth: 2
        }
      ]
    };
  }

  public readonly sparklineOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false }
    },
    scales: {
      x: { display: false },
      y: { display: false }
    },
    elements: {
      point: { radius: 0 }
    }
  };

  public isDeltaPositive(metric: 'clicks' | 'impressions' | 'ctr' | 'avgPosition', delta: number): boolean {
    // For avgPosition lower is better, so a negative delta (rank improved) is positive
    if (metric === 'avgPosition') {
      return delta < 0;
    }
    return delta >= 0;
  }

  public formatDelta(metric: 'clicks' | 'impressions' | 'ctr' | 'avgPosition', kpi: GscKpi): string {
    const sign = kpi.delta >= 0 ? '+' : '';
    if (metric === 'ctr') {
      return `${sign}${(kpi.delta * 100).toFixed(2)}pp`;
    }
    if (metric === 'avgPosition') {
      // Invert sign display: position improved (lower) shows as positive
      const displaySign = kpi.delta <= 0 ? '+' : '';
      return `${displaySign}${Math.abs(kpi.delta).toFixed(1)} pos`;
    }
    if (Math.abs(kpi.deltaPercent) > 0.001) {
      return `${sign}${kpi.deltaPercent.toFixed(1)}%`;
    }
    return `${sign}${kpi.delta}`;
  }

  public formatCtr(value: number): string {
    return `${(value * 100).toFixed(2)}%`;
  }

  public formatPosition(value: number): string {
    return value.toFixed(1);
  }

  private async loadData(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const { currentStart, currentEnd, previousStart, previousEnd } = this.buildDateRanges();

      const [currentResponse, previousResponse] = await Promise.all([
        this.fetchPeriod(currentStart, currentEnd),
        this.fetchPeriod(previousStart, previousEnd)
      ]);

      this.summary = this.buildSummary(currentResponse, previousResponse);
    } catch (error) {
      this.utils.logger.info('GscDashboardComponent.loadData() error:', error);
      this.authorizationRequired = isGoogleAuthorizationError(error);
      this.hasError = !this.authorizationRequired;
      if (this.authorizationRequired) {
        this.clearCachedGoogleAuthorization();
      }
    } finally {
      this.isLoading = false;
      this.emitState();
    }
  }

  private buildDateRanges(): { currentStart: string; currentEnd: string; previousStart: string; previousEnd: string } {
    const today = new Date();
    const currentEnd = this.toDateString(today);
    const currentStart = this.toDateString(this.subtractDays(today, 27));
    const previousEnd = this.toDateString(this.subtractDays(today, 28));
    const previousStart = this.toDateString(this.subtractDays(today, 55));

    return { currentStart, currentEnd, previousStart, previousEnd };
  }

  private async fetchPeriod(startDate: string, endDate: string): Promise<any[]> {
    const project = this.project;
    const google = project?.externalProviders?.google;

    const params = {
      siteUrl: google?.siteUrl,
      projectId: project?.id,
      startDate,
      endDate,
      dimensions: ['date']
    };

    const { data, error } = await this.cache.getSearchConsoleData(params);

    if (error) {
      throw error;
    }
    if (!data) {
      throw new Error('Search Console returned no data.');
    }

    return Array.isArray(data?.rows) ? data.rows : [];
  }

  private buildSummary(currentRows: any[], previousRows: any[]): GscSummary {
    const current = this.aggregateRows(currentRows);
    const previous = this.aggregateRows(previousRows);

    const sortedDates = [...currentRows].sort((a, b) =>
      (a.keys?.[0] ?? '').localeCompare(b.keys?.[0] ?? '')
    );

    return {
      clicks: this.buildKpi(
        current.clicks,
        previous.clicks,
        sortedDates.map(r => r.clicks ?? 0),
        sortedDates.map(r => r.keys?.[0] ?? '')
      ),
      impressions: this.buildKpi(
        current.impressions,
        previous.impressions,
        sortedDates.map(r => r.impressions ?? 0),
        sortedDates.map(r => r.keys?.[0] ?? '')
      ),
      ctr: this.buildKpi(
        current.impressions > 0 ? current.clicks / current.impressions : 0,
        previous.impressions > 0 ? previous.clicks / previous.impressions : 0,
        sortedDates.map(r => r.ctr ?? 0),
        sortedDates.map(r => r.keys?.[0] ?? '')
      ),
      avgPosition: this.buildKpi(
        current.weightedPosition,
        previous.weightedPosition,
        sortedDates.map(r => r.position ?? 0),
        sortedDates.map(r => r.keys?.[0] ?? '')
      )
    };
  }

  private aggregateRows(rows: any[]): { clicks: number; impressions: number; weightedPosition: number } {
    let clicks = 0;
    let impressions = 0;
    let positionSum = 0;
    let positionImpressions = 0;

    for (const row of rows) {
      const rowImpressions = row.impressions ?? 0;
      clicks += row.clicks ?? 0;
      impressions += rowImpressions;
      positionSum += (row.position ?? 0) * rowImpressions;
      positionImpressions += rowImpressions;
    }

    const weightedPosition = positionImpressions > 0 ? positionSum / positionImpressions : 0;
    return { clicks, impressions, weightedPosition };
  }

  private buildKpi(current: number, previous: number, sparklineValues: number[], sparklineDates: string[]): GscKpi {
    const delta = current - previous;
    const deltaPercent = previous !== 0 ? (delta / previous) * 100 : 0;
    return { current, previous, delta, deltaPercent, sparklineValues, sparklineDates };
  }

  private subtractDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
  }

  private toDateString(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private async resolveSelectedProject(): Promise<any> {
    if (this.cache?.selectedProject?.id) {
      return this.cache.selectedProject;
    }

    const currentProject = this.cache.currentProject;
    if (!currentProject?.id) {
      return null;
    }

    const orgId = this.cache.organization?.id ?? this.cache.user?.currentOrganizationId;

    let result: any;
    if (orgId) {
      result = await this.cache.getProjectByOrg(currentProject.id, String(orgId));
    } else {
      result = await this.cache.getProject(currentProject.id, this.cache.userId);
    }

    if (result?.data) {
      this.cache.selectedProject = result.data;
      return result.data;
    }

    return null;
  }

  private emitState(): void {
    this.stateChange.emit({
      connected: this.isConnected && !this.authorizationRequired,
      error: this.hasError,
      authorizationRequired: this.authorizationRequired,
      summary: this.summary
    });
  }

  private clearCachedGoogleAuthorization(): void {
    const google = this.project?.externalProviders?.google;
    if (!google) {
      return;
    }

    google.accessToken = '';
    google.refreshToken = '';
  }
}
